import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { useScrollReveal } from '../../../hooks/useScrollReveal'
import { useLang } from '../../../context/AppContext'

const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8888'
const API_URL = `${BASE_URL}/api/v1`

interface Ad {
  id: number
  title?: string
  subtitle?: string
  title_ru?: string
  subtitle_ru?: string
  image?: string
}

const BG_PALETTE = [
  'from-primary via-emerald-500 to-teal-600',
  'from-slate-800 via-slate-700 to-slate-900',
  'from-amber-500 via-orange-500 to-red-500',
  'from-violet-600 via-purple-600 to-indigo-600',
  'from-sky-500 via-cyan-500 to-teal-500',
]

// The always-on brand hero. Everything the visitor should read on this slide
// (logo, tagline, feature icons, "Найти ближайший магазин" CTA) is BAKED INTO
// the image, so this slide never renders an overlay panel — `hero: true` tells
// the renderer to show the image alone. Clicking it navigates to /shops
// (which is what the baked-in CTA promises).
//
// SINGLE SOURCE OF TRUTH: the image is served from the backend's static folder
// (also read by the mobile app via the /ad/all row it belongs to). Replacing
// hero.webp on the server therefore updates BOTH the website and every
// installed mobile app at once — no rebuild, no store update.
const HERO_BANNER = {
  bg: 'from-emerald-700 via-emerald-800 to-emerald-900',
  badge: { uz: '', ru: '' },
  title: { uz: '', ru: '' },
  desc: { uz: '', ru: '' },
  cta: { uz: '', ru: '' },
  image: 'https://api.diametr.uz/static/ads/hero.webp',
  hero: true as const,
  href: '/shops',
}

const FALLBACK_BANNERS = [
  {
    bg: 'from-primary via-emerald-500 to-teal-600',
    badge: { uz: '🏗️ Qurilish', ru: '🏗️ Строительство' },
    title: { uz: 'Qurilish materiallari — eng yaxshi narxlarda', ru: 'Стройматериалы — по лучшим ценам' },
    desc: { uz: "O'zbekistondagi yetakchi qurilish bozori. 10,000+ mahsulot, 500+ do'kon.", ru: 'Ведущий строительный рынок Узбекистана. 10,000+ товаров, 500+ магазинов.' },
    cta: { uz: "Katalogni ko'rish", ru: 'Смотреть каталог' },
    image: null as string | null,
  },
  {
    bg: 'from-slate-800 via-slate-700 to-slate-900',
    badge: { uz: '🚚 Yetkazib berish', ru: '🚚 Доставка' },
    title: { uz: 'Tez va ishonchli yetkazib berish', ru: 'Быстрая и надёжная доставка' },
    desc: { uz: "Butun O'zbekiston bo'ylab tezkor yetkazib berish. Buyurtmangizni onlayn bering!", ru: 'Быстрая доставка по всему Узбекистану. Оформите заказ онлайн!' },
    cta: { uz: 'Ilovani yuklab olish', ru: 'Скачать приложение' },
    image: null as string | null,
  },
  {
    bg: 'from-amber-500 via-orange-500 to-red-500',
    badge: { uz: '⭐ Aksiyalar', ru: '⭐ Акции' },
    title: { uz: 'Chegirma va aksiyalar har kuni', ru: 'Скидки и акции каждый день' },
    desc: { uz: "Mahalliy do'konlardan eng katta chegirmalar. Vaqtni boy bermang!", ru: 'Лучшие скидки от местных магазинов. Не упустите возможность!' },
    cta: { uz: "Do'konlarni ko'rish", ru: 'Смотреть магазины' },
    image: null as string | null,
  },
]

export default function BannerSlider() {
  const { lang } = useLang()
  const navigate = useNavigate()
  const [current, setCurrent] = useState(0)
  const [animating, setAnimating] = useState(false)
  const [ads, setAds] = useState<Ad[]>([])
  const [loaded, setLoaded] = useState(false)
  const ref = useScrollReveal()

  useEffect(() => {
    axios.get(`${API_URL}/ad/all`)
      .then(res => {
        const d = res.data?.data ?? res.data
        const list = Array.isArray(d) ? d.filter((a: Ad) => a.image) : []
        setAds(list)
      })
      .catch(() => setAds([]))
      .finally(() => setLoaded(true))
  }, [])

  const realBanners = loaded && ads.length > 0
    ? ads.map((ad, i) => ({
        bg: BG_PALETTE[i % BG_PALETTE.length],
        badge: { uz: ad.title || 'Reklama', ru: ad.title_ru || ad.title || 'Реклама' },
        title: { uz: ad.title || '', ru: ad.title_ru || ad.title || '' },
        desc: { uz: ad.subtitle || '', ru: ad.subtitle_ru || ad.subtitle || '' },
        cta: { uz: "Ko'proq", ru: 'Подробнее' },
        image: `${BASE_URL}/static/ads/${ad.image}`,
      }))
    : []

  // Only the brand HERO — asked to be the single banner. Real ads and the
  // branded fallback slides are kept in code (still fetched above so the
  // reklama admin flow keeps working end-to-end) but are not shown in the
  // slider; to bring them back, append `...realBanners` (or `FALLBACK_BANNERS`)
  // to the array below.
  const banners = [HERO_BANNER]
  void realBanners // referenced above to keep the fetch honest — silence unused-var

  const total = banners.length

  const goTo = useCallback((idx: number) => {
    if (animating) return
    setAnimating(true)
    setTimeout(() => {
      setCurrent(idx)
      setAnimating(false)
    }, 350)
  }, [animating])

  useEffect(() => {
    // Only auto-advance when there is more than one banner — otherwise the
    // single banner needlessly re-animates (fades out/in) every 5s.
    if (!loaded || total <= 1) return
    const timer = setInterval(() => goTo((current + 1) % total), 5000)
    return () => clearInterval(timer)
  }, [current, goTo, total, loaded])

  const banner = banners[current]
  const scrollTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })

  // While the ads are loading, show a neutral skeleton instead of the coloured
  // fallback hero — otherwise the page flashes the fallback and then swaps to
  // the real banner ("ochib yonadi").
  if (!loaded) {
    return (
      <section ref={ref} className="w-full px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="w-full rounded-3xl bg-gray-100 dark:bg-white/5 animate-pulse h-[200px] sm:h-[260px] lg:h-[320px]" />
        </div>
      </section>
    )
  }

  return (
    <section ref={ref} className="w-full px-4 sm:px-6 lg:px-8 py-6">
      <div className="max-w-7xl mx-auto">
        <div
          className={`relative w-full rounded-3xl overflow-hidden transition-all duration-500 bg-gradient-to-r ${banner.bg}`}
        >
          {banner.image ? (
            // Full-width hero at a capped height, anchored to the TOP so the
            // logo, headline and CTA always show; only the lower product rows
            // get trimmed. Fills the row edge-to-edge — no floating box.
            //
            // For the branded HERO the whole slide is a link (href → the CTA
            // baked into the image); other image ads render as a plain <img>
            // because clicking them today has no defined destination. The
            // hero/href fields are read via a cast because CRA's older TS
            // narrows the discriminated union to `never` inside `in` checks.
            (() => {
              const b = banner as { hero?: boolean; href?: string }
              const isHero = b.hero === true
              const heroHref: string | undefined = typeof b.href === 'string' ? b.href : undefined
              const img = (
                <img
                  src={banner.image}
                  alt={isHero ? 'Diametr.uz — Найти ближайший магазин' : (banner.title[lang] ?? '')}
                  loading="eager"
                  decoding="async"
                  className={
                    isHero
                      // Hero: web renders at a 16:6 slot (matching the raw
                      // hero content — logo, tagline, category strip, feature
                      // row, CTA). The file on disk is a 16:9 padded version
                      // (so the mobile 16:9 carousel fits perfectly without
                      // cropping); object-cover object-center clips exactly
                      // the padding zones, leaving only the hero content on
                      // web. Single source of truth for both platforms.
                      ? `block w-full aspect-[16/6] object-cover object-center transition-opacity duration-350 ${animating ? 'opacity-0' : 'opacity-100'}`
                      // Ad banners: fixed height and object-cover so ads of
                      // any aspect ratio all fill the same slider slot.
                      : `block w-full h-[200px] sm:h-[300px] lg:h-[400px] object-cover object-top transition-opacity duration-350 ${animating ? 'opacity-0' : 'opacity-100'}`
                  }
                />
              )
              if (isHero && heroHref) {
                return (
                  <button
                    type="button"
                    onClick={() => navigate(heroHref)}
                    aria-label="Diametr.uz — magazinlarni ko'rish"
                    className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-emerald-900"
                  >
                    {img}
                  </button>
                )
              }
              return img
            })()
          ) : (
            /* Fallback hero — gradient background with text + CTA */
            <div
              className={`flex flex-col justify-center h-[200px] sm:h-[260px] lg:h-[320px] px-8 sm:px-16 max-w-2xl transition-all duration-350 ${
                animating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
              }`}
            >
              <span className="inline-flex items-center gap-2 bg-white/20 text-white font-semibold text-xs px-3 py-1.5 rounded-full mb-4 w-fit backdrop-blur-sm">
                {banner.badge[lang]}
              </span>
              <h2 className="text-white font-bold text-2xl sm:text-3xl lg:text-4xl leading-tight mb-3 drop-shadow">
                {banner.title[lang]}
              </h2>
              {banner.desc[lang] && (
                <p className="text-white/80 text-sm sm:text-base mb-6 max-w-lg leading-relaxed">
                  {banner.desc[lang]}
                </p>
              )}
              <button
                onClick={() => scrollTo('CategoryGrid')}
                className="inline-flex items-center gap-2 bg-white/95 text-slate-800 font-semibold px-6 py-2.5 rounded-xl hover:bg-white transition-all duration-200 hover:-translate-y-0.5 shadow-lg w-fit text-sm"
              >
                {banner.cta[lang]}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </button>
            </div>
          )}

          {/* Dots */}
          {total > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
              {banners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`rounded-full transition-all duration-300 ${
                    i === current ? 'w-6 h-2.5 bg-white' : 'w-2.5 h-2.5 bg-white/60 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Prev/Next arrows */}
          {total > 1 && (
            <>
              <button
                onClick={() => goTo((current - 1 + total) % total)}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 backdrop-blur-sm flex items-center justify-center text-white transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                </svg>
              </button>
              <button
                onClick={() => goTo((current + 1) % total)}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 backdrop-blur-sm flex items-center justify-center text-white transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  )
}
